/**
 * Runtime config is injected by nginx entrypoint from VITE_API_URL environment.
 * Falls back to build-time VITE_API_URL for development.
 * Same-origin fallback (/api) keeps tests independent from a particular environment.
 */
function getApiUrl(): string {
  // Check runtime config injected by nginx (appConfig.apiUrl)
  const runtimeApiUrl = typeof window !== 'undefined' ? window.appConfig?.apiUrl : undefined
  if (runtimeApiUrl) {
    return runtimeApiUrl.trim().replace(/\/$/, '')
  }

  // Fall back to build-time variable for local development
  const buildTimeUrl = import.meta.env.VITE_API_URL?.trim().replace(/\/$/, '')
  if (buildTimeUrl) return buildTimeUrl

  // Default to same-origin API for tests and local dev
  return '/api'
}

export const API_URL = getApiUrl()

/**
 * Reads a real browser Response and the lightweight Response-shaped objects
 * used by the automated tests. Keeping this compatibility here also makes the
 * client tolerant of environments that expose only one body reader.
 */
async function readApiBody(response: Response): Promise<unknown> {
  if (typeof response.text === 'function') {
    const rawBody = await response.text()
    if (!rawBody.trim()) return undefined

    try {
      return JSON.parse(rawBody)
    } catch {
      return rawBody
    }
  }

  if (typeof response.json === 'function') {
    try {
      return await response.json()
    } catch {
      return undefined
    }
  }

  return undefined
}

/**
 * Converts API failures into a message that is safe to show in the portal.
 * A plain "Not Found" is not useful to an administrator and often means the
 * browser is talking to an older API process, so explain the next action.
 */
export async function getApiErrorMessage(response: Response): Promise<string> {
  const body = await readApiBody(response)
  const bodyCorrelationId = typeof (body as { correlation_id?: unknown } | undefined)?.correlation_id === 'string'
    ? (body as { correlation_id: string }).correlation_id
    : ''
  const headerCorrelationId = typeof response.headers?.get === 'function'
    ? response.headers.get('X-Correlation-ID') || ''
    : ''
  const correlationId = bodyCorrelationId || headerCorrelationId
  const detail = typeof body === 'string'
    ? (body.trim().startsWith('<') ? '' : body.trim())
    : typeof (body as { detail?: unknown } | undefined)?.detail === 'string'
      ? (body as { detail: string }).detail
      : ''

  if (response.status === 404 && (!detail || /^not found$/i.test(detail))) {
    return 'This action is not available in the API currently running. Refresh the portal and try again. If it continues, restart the API so it loads the latest routes.'
  }

  const message = detail || `Request failed with HTTP ${response.status}`
  return correlationId ? `${message} (reference ${correlationId})` : message
}

export async function apiGet<T>(path: string, options: { timeoutMs?: number } = {}): Promise<T> {
  const correlationId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), options.timeoutMs ?? 30_000)
  try {
    const response = await fetch(`${API_URL}${path}`, {
      headers: { 'X-Correlation-ID': correlationId },
      signal: controller.signal,
    })
    if (!response.ok) {
      throw new Error(await getApiErrorMessage(response))
    }
    const body = await readApiBody(response)
    if (body === undefined) return undefined as T
    if (typeof body === 'string') {
      throw new Error('The server returned an unexpected response. Refresh the portal and try again.')
    }
    return body as T
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(
        'The request took too long. Check the service status, then retry this action.',
        { cause: error },
      )
    }
    throw error
  } finally {
    window.clearTimeout(timeout)
  }
}

export const queryKeys = {
  incidents: ['incidents'] as const,
  articles: ['articles'] as const,
  user: ['current-user'] as const,
  confluenceStatus: ['confluence-status'] as const,
  confluenceImportSettings: ['confluence-import-settings'] as const,
  sharePointStatus: ['sharepoint-status'] as const,
  sharePointConnectorSettings: ['sharepoint-connector-settings'] as const,
  organizationMapping: ['organization-mapping'] as const,
  sharePointDocuments: ['sharepoint-documents'] as const,
  jobs: ['background-jobs'] as const,
  savedAnswers: (userName: string) => ['saved-ai-answers', userName] as const,
  ingestionRuns: ['ingestion-runs'] as const,
  fileDropStatus: ['file-drop-status'] as const,
  testRuns: ['automated-test-runs'] as const,
  notifications: (userName: string) => ['portal-notifications', userName] as const,
  supportTickets: (userName: string, role: string) => ['portal-support-tickets', userName, role] as const,
  supportTicketComments: (ticketId: number) => ['portal-support-ticket-comments', ticketId] as const,
  reviewComments: (entityType: 'incident' | 'article', id: number) => ['review-comments', entityType, id] as const,
  portalSettings: (userName: string) => ['portal-settings', userName] as const,
  systemStatus: ['system-status'] as const,
  systemHealth: ['system-health'] as const,
  systemActivity: ['system-activity'] as const,
  aiUsage: ['ai-usage'] as const,
  aiUsageLifetime: ['ai-usage-lifetime'] as const,
  aiCostRates: ['ai-cost-rates'] as const,
  userActivity: ['user-activity'] as const,
  aiProviderStatus: ['ai-provider-status'] as const,
  ingestionSettings: ['ingestion-settings'] as const,
  dataQuality: ['data-quality'] as const,
  dataQualityIssues: (page: number) => ['data-quality-issues', page] as const,
  dataQualityReport: ['data-quality-report'] as const,
  importQualityReport: ['import-quality-report'] as const,
  etrmDocumentReport: (min: number, max: number) => ['etrm-document-report', min, max] as const,
  staleDuplicatesReport: ['stale-duplicates-report'] as const,
  searchIndexCounts: ['search-index-counts'] as const,
  analytics: (range: string) => ['analytics', range] as const,
  changelog: ['changelog'] as const,
}
